import { useEffect, useState, useCallback } from "react";
import { Crown, KeyRound, Trash2, Save, UserCog } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Loader } from "@/components/Page";
import { Modal, Label, inputCls, RARITIES } from "./shared";

export function ManagerModal({ userId, onClose, onChanged, onOpenGlad, onFilterIp }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/admin/managers/${userId}`).then((r) => {
    setData(r.data); setForm({ name: r.data.manager.name, email: r.data.manager.email, role: r.data.manager.role, vip: r.data.manager.vip, vip_status: r.data.manager.vip_status || "", fragments: r.data.manager.fragments, gold: r.data.manager.gold ?? 0, follis: r.data.manager.follis ?? 0, admin_bonus_slots: r.data.manager.admin_bonus_slots ?? 0 });
  }).catch((e) => toast.error(apiErr(e))), [userId]);
  useEffect(() => { load(); }, [load]);

  if (!form) return <Modal title="Manager" onClose={onClose} testid="manager-modal"><Loader /></Modal>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFrag = (kind, r, v) => setForm((f) => ({ ...f, fragments: { ...f.fragments, [kind]: { ...f.fragments[kind], [r]: Number(v) } } }));

  const save = async () => {
    setBusy(true);
    try { await api.patch(`/admin/managers/${userId}`, { name: form.name, email: form.email, role: form.role, vip: form.vip, vip_status: form.vip_status || null, fragments: form.fragments, gold: Number(form.gold), follis: Number(form.follis), admin_bonus_slots: Number(form.admin_bonus_slots) }); toast.success("Manager updated"); onChanged(); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const resetPw = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    try { await api.post(`/admin/managers/${userId}/reset-password`, { new_password: pw }); toast.success("Password reset"); setPw(""); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${form.name} and ALL their gladiators? This cannot be undone.`)) return;
    try { await api.delete(`/admin/managers/${userId}`); toast.success("Manager deleted"); onChanged(); onClose(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const impersonate = async () => {
    if (!window.confirm(`Login as ${form.name}? This will log you into their account for debugging purposes. This action is logged for security.`)) return;
    setBusy(true);
    try {
      const response = await api.post(`/admin/impersonate/${userId}`);
      const token = response.data.token;
      
      // Store the new token
      localStorage.setItem("token", token);
      
      // Show success message
      toast.success(`Now logged in as ${form.name}`, {
        description: "You are now impersonating this user. Refresh to see their account."
      });
      
      // Reload the page to apply the new token
      setTimeout(() => {
        window.location.href = "/";
      }, 1500);
    } catch (e) {
      toast.error(apiErr(e));
      setBusy(false);
    }
  };

  return (
    <Modal title={`Manager · ${data.manager.name}`} onClose={onClose} testid="manager-modal">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Name</Label><input data-testid="m-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div><Label>Email</Label><input data-testid="m-email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
        <div><Label>Role</Label>
          <select data-testid="m-role" className={inputCls} value={form.role} onChange={(e) => set("role", e.target.value)}>
            <option value="player">player</option><option value="admin">admin</option>
          </select>
        </div>
        <div><Label>VIP Status</Label><input data-testid="m-vip-status" className={inputCls} value={form.vip_status} onChange={(e) => set("vip_status", e.target.value)} placeholder="e.g. ACTIVE" /></div>
        <div><Label>Gold</Label><input data-testid="m-gold" type="number" min={0} className={inputCls} value={form.gold} onChange={(e) => set("gold", e.target.value)} /></div>
        <div><Label>Follis</Label><input data-testid="m-follis" type="number" min={0} className={inputCls} value={form.follis} onChange={(e) => set("follis", e.target.value)} /></div>
        <div><Label>Bonus Gladiator Slots</Label><input data-testid="m-bonus-slots" type="number" min={0} max={50} className={inputCls} value={form.admin_bonus_slots} onChange={(e) => set("admin_bonus_slots", e.target.value)} /></div>
      </div>
      <p className="mt-1 text-[11px] text-zinc-500" data-testid="m-roster-cap">
        Roster cap: <span className="text-amber-300 tabular-nums">{data.manager.roster_cap}</span> gladiators
        <span className="text-zinc-600"> (permanent bonus slots stack on top of base &amp; VIP; not capped by the paid store limit)</span>
      </p>
      <label className="flex items-center gap-2 mt-4 text-sm text-zinc-300 cursor-pointer">
        <input data-testid="m-vip" type="checkbox" checked={form.vip} onChange={(e) => set("vip", e.target.checked)} className="accent-amber-500 w-4 h-4" />
        <Crown size={14} className="text-amber-400" /> VIP member (grants all VIP perks)
      </label>

      {(data.manager.paypal_subscription_id || data.manager.vip_since) && (
        <div className="mt-3 p-3 rounded-sm border border-amber-900/40 bg-amber-950/10 text-xs space-y-1" data-testid="m-vip-sub">
          <p className="text-zinc-400">Active subscription (app-tracked): {data.manager.paypal_subscription_id ? (
            <a data-testid="m-vip-sub-link" href={`https://www.paypal.com/billing/subscriptions/${data.manager.paypal_subscription_id}`} target="_blank" rel="noreferrer"
              className="text-amber-300 hover:text-amber-200 underline tabular-nums break-all">{data.manager.paypal_subscription_id}</a>
          ) : <span className="text-zinc-500">— none on file —</span>}</p>
          <p className="text-zinc-500">VIP since: <span className="text-zinc-300">{data.manager.vip_since ? new Date(data.manager.vip_since).toLocaleString() : "—"}</span> · Status: <span className="text-zinc-300">{data.manager.vip_status || "—"}</span></p>
          <p className="text-zinc-600">This is the subscription the app honors for VIP. If the player was billed twice, refund the PayPal subscription that does NOT match this ID.</p>
        </div>
      )}

      <p className="mt-3 text-[11px] text-zinc-500" data-testid="m-last-login">
        Last login: <span className="text-zinc-300">{data.manager.last_login ? new Date(data.manager.last_login).toLocaleString() : "—"}</span>
      </p>

      {(data.manager.ip_history?.length > 0 || data.manager.last_ip) && (
        <div className="mt-5" data-testid="m-ip-history">
          <Label>Recent IP Addresses</Label>
          <div className="flex flex-wrap gap-2">
            {(data.manager.ip_history?.length ? [...data.manager.ip_history].reverse() : [{ ip: data.manager.last_ip, at: null }]).map((h, i) => (
              <button key={i} data-testid={`m-ip-${i}`} onClick={() => onFilterIp?.(h.ip)}
                title={h.at ? `Seen ${new Date(h.at).toLocaleString()} — click to view accounts on this IP` : "Click to view accounts on this IP"}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs tabular-nums bg-zinc-950 border border-zinc-700 text-zinc-300 hover:border-amber-600 hover:text-amber-300 transition-colors">
                {h.ip}{h.at && <span className="text-zinc-600">· {new Date(h.at).toLocaleDateString()}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5">
        <Label>Fragments</Label>
        <div className="space-y-2">
          {["weapon", "armor"].map((kind) => (
            <div key={kind} className="flex items-center gap-2">
              <span className="w-16 text-xs uppercase text-zinc-500">{kind}</span>
              <div className="grid grid-cols-5 gap-1.5 flex-1">
                {RARITIES.map((r) => (
                  <div key={r}>
                    <div className="text-[9px] uppercase text-zinc-600 mb-0.5">{r.slice(0, 4)}</div>
                    <input data-testid={`m-frag-${kind}-${r}`} type="number" className={`${inputCls} px-2 py-1.5 text-xs`} value={form.fragments?.[kind]?.[r] ?? 0} onChange={(e) => setFrag(kind, r, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2 mt-5 pt-4 border-t border-zinc-800">
        <div className="flex-1 min-w-[160px]"><Label>Reset Password</Label><input data-testid="m-newpw" className={inputCls} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" /></div>
        <button data-testid="m-reset-pw-btn" onClick={resetPw} className="flex items-center gap-1.5 px-3 py-2 border border-amber-900/40 text-amber-300 hover:border-amber-600 text-sm rounded-sm"><KeyRound size={14} /> Reset</button>
      </div>

      <div className="mt-4 p-3 rounded-sm border border-blue-900/40 bg-blue-950/10">
        <Label>🔧 Debug Tools</Label>
        <p className="text-xs text-zinc-500 mb-3">Login as this user to reproduce bugs and test features from their perspective. All impersonations are logged.</p>
        <button 
          data-testid="m-impersonate-btn" 
          onClick={impersonate} 
          disabled={busy || data.manager.is_admin}
          className="flex items-center gap-2 px-4 py-2 border border-blue-700 text-blue-300 hover:border-blue-500 hover:bg-blue-950/30 text-sm rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          <UserCog size={15} /> Impersonate User
        </button>
        {data.manager.is_admin && (
          <p className="text-xs text-red-400 mt-2">⚠️ Cannot impersonate admin accounts</p>
        )}
      </div>

      {data.gladiators.length > 0 && (
        <div className="mt-5">
          <Label>Gladiators ({data.gladiators.length})</Label>
          <div className="flex flex-wrap gap-2">
            {data.gladiators.map((g) => (
              <button key={g.id} data-testid={`m-glad-${g.id}`} onClick={() => onOpenGlad(g.id)}
                className={`text-xs px-2.5 py-1.5 border rounded-sm hover:border-amber-600 ${g.retired ? "border-zinc-800 text-zinc-500" : "border-amber-900/40 text-zinc-300"}`}>
                {g.name} · {g.rating}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t border-zinc-800">
        <button data-testid="m-delete-btn" onClick={del} className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-red-400"><Trash2 size={15} /> Delete account</button>
        <button data-testid="m-save-btn" onClick={save} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-amber-400 disabled:opacity-50 rounded-sm"><Save size={15} /> Save</button>
      </div>
    </Modal>
  );
}
